import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * ThemeProvider - Manages light/dark theme with optional server persistence.
 * @param {object} props
 * @param {object} props.apiService - API service instance with getUserSettings/updateUserSettings
 * @param {React.ReactNode} props.children
 */
export const ThemeProvider = ({ apiService, children }) => {
  const [theme, setTheme] = useState('light');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUserSettings();
  }, []);

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  const loadUserSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setTheme('light');
        setLoading(false);
        return;
      }
      const settings = await apiService.getUserSettings();
      setTheme(settings.theme || 'light');
    } catch (error) {
      console.error('Failed to load user settings:', error);
      setTheme('light');
    } finally {
      setLoading(false);
    }
  };

  const toggleTheme = async () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    try {
      await apiService.updateUserSettings({ theme: newTheme });
    } catch (error) {
      console.error('Failed to update theme:', error);
      setTheme(theme);
    }
  };

  const setThemeValue = async (newTheme) => {
    if (newTheme !== 'light' && newTheme !== 'dark') return;
    setTheme(newTheme);
    try {
      await apiService.updateUserSettings({ theme: newTheme });
    } catch (error) {
      console.error('Failed to update theme:', error);
      setTheme(theme);
    }
  };

  const value = {
    theme,
    loading,
    toggleTheme,
    setTheme: setThemeValue,
    refreshSettings: loadUserSettings,
    apiService,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export default ThemeContext;
